const FIELD_TYPES = new Set(['short_text', 'long_text', 'number', 'radio', 'checkbox', 'select', 'date']);
const CHOICE_TYPES = new Set(['radio', 'checkbox', 'select']);

export function getUnsupportedSchemaReason(schema) {
  if (!schema) return 'Schema missing';
  if (schema.schema_version !== 1) return 'Unsupported schema version';
  if (Array.isArray(schema.sections)) {
    for (const section of schema.sections) {
      if (Array.isArray(section.fields)) {
        for (const field of section.fields) {
          if (!FIELD_TYPES.has(field.type)) {
            return `Unsupported field type: ${field.type}`;
          }
        }
      }
    }
  }
  return null;
}

const error = (path, message, fieldId = null) => ({ path, fieldId, message });
const bytes = value => typeof TextEncoder !== 'undefined' ? new TextEncoder().encode(JSON.stringify(value)).length : Buffer.byteLength(JSON.stringify(value));
const dateValue = value => /^\d{4}-\d{2}-\d{2}$/.test(value || '') ? value : null;
const validationKeys = {
  short_text: ['min_length', 'max_length'], long_text: ['min_length', 'max_length'], number: ['min', 'max'],
  radio: [], select: [], checkbox: ['min_selections', 'max_selections'], date: ['earliest', 'latest'],
};

function validateFieldConfig(field, path) {
  const rules = field.validation || {};
  const errors = Object.keys(rules).filter(key => !(validationKeys[field.type] || []).includes(key))
    .map(key => error(`${path}.validation.${key}`, 'Unsupported validation key', field.id));
  if (['short_text', 'long_text'].includes(field.type)) {
    const cap = field.type === 'short_text' ? 500 : 10000;
    const minimum = rules.min_length ?? 0;
    const maximum = rules.max_length ?? cap;
    if (!Number.isInteger(minimum) || !Number.isInteger(maximum) || minimum < 0 || maximum < minimum || maximum > cap) {
      errors.push(error(`${path}.validation`, `Text length must satisfy 0 <= minimum <= maximum <= ${cap}`, field.id));
    }
  } else if (field.type === 'number' && rules.min != null && rules.max != null && rules.min > rules.max) {
    errors.push(error(`${path}.validation`, 'Number minimum cannot exceed maximum', field.id));
  } else if (field.type === 'checkbox') {
    const minimum = rules.min_selections ?? 0;
    const maximum = rules.max_selections ?? (field.options || []).length;
    if (!Number.isInteger(minimum) || !Number.isInteger(maximum) || minimum < 0 || maximum < minimum || maximum > (field.options || []).length) {
      errors.push(error(`${path}.validation`, 'Selection limits must fit the available options', field.id));
    }
  } else if (field.type === 'date' && (rules.earliest && !dateValue(rules.earliest) || rules.latest && !dateValue(rules.latest) || rules.earliest && rules.latest && rules.earliest > rules.latest)) {
    errors.push(error(`${path}.validation`, 'Date limits must be ordered YYYY-MM-DD values', field.id));
  }
  return errors;
}

export function validateSchema(schema) {
  const errors = [];
  if (schema?.schema_version !== 1) errors.push(error('schema_version', 'Unsupported schema version'));
  if (bytes(schema) > 2 * 1024 * 1024) errors.push(error('schema', 'Template schema exceeds 2 MiB'));
  const sections = schema?.sections;
  if (!Array.isArray(sections) || sections.length === 0) return [...errors, error('sections', 'A template requires at least one section')];
  if (sections.length > 50) errors.push(error('sections', 'A template may contain at most 50 sections'));
  const sectionIds = new Set();
  const fieldIds = new Set();
  let fieldCount = 0;
  let optionCount = 0;
  sections.forEach((section, sectionIndex) => {
    if (!section.id || sectionIds.has(section.id)) errors.push(error(`sections.${sectionIndex}.id`, section.id ? 'Duplicate section ID' : 'Section ID is required'));
    sectionIds.add(section.id);
    if (!section.title?.trim() || section.title.length > 200) errors.push(error(`sections.${sectionIndex}.title`, 'Section title must contain 1 to 200 characters'));
    if ((section.description || '').length > 2000) errors.push(error(`sections.${sectionIndex}.description`, 'Section description may contain at most 2000 characters'));
    (section.fields || []).forEach((field, fieldIndex) => {
      fieldCount += 1;
      const path = `sections.${sectionIndex}.fields.${fieldIndex}`;
      if (!field.id || fieldIds.has(field.id)) errors.push(error(`${path}.id`, field.id ? 'Duplicate field ID' : 'Field ID is required', field.id));
      fieldIds.add(field.id);
      if (!FIELD_TYPES.has(field.type)) errors.push(error(`${path}.type`, 'Unsupported field type', field.id));
      if (!field.label?.trim() || field.label.length > 300) errors.push(error(`${path}.label`, 'Field label must contain 1 to 300 characters', field.id));
      if ((field.help_text || '').length > 2000) errors.push(error(`${path}.help_text`, 'Help text may contain at most 2000 characters', field.id));
      const options = field.options || [];
      optionCount += options.length;
      if (CHOICE_TYPES.has(field.type) && options.length === 0) errors.push(error(`${path}.options`, 'Choice field requires at least one option', field.id));
      if (options.length > 100) errors.push(error(`${path}.options`, 'A choice field may contain at most 100 options', field.id));
      const optionIds = new Set();
      options.forEach((option, optionIndex) => {
        if (!option.id || optionIds.has(option.id)) errors.push(error(`${path}.options.${optionIndex}.id`, 'Option IDs must be non-empty and unique', field.id));
        optionIds.add(option.id);
        if (!option.label?.trim() || option.label.length > 300) errors.push(error(`${path}.options.${optionIndex}.label`, 'Option label must contain 1 to 300 characters', field.id));
      });
      errors.push(...validateFieldConfig(field, path));
    });
    if ((section.fields || []).length === 0) errors.push(error(`sections.${sectionIndex}.fields`, 'A section requires at least one field'));
  });
  if (fieldCount > 500) errors.push(error('sections', 'A template may contain at most 500 fields'));
  if (optionCount > 2000) errors.push(error('sections', 'A template may contain at most 2000 choice options'));
  return errors;
}

export function validateAnswers(schema, answers) {
  if (!answers || Array.isArray(answers) || typeof answers !== 'object') return [error('answers', 'Answers must be an object')];
  if (bytes(answers) > 5 * 1024 * 1024) return [error('answers', 'Assessment answers exceed 5 MiB')];
  const fields = new Map(schema.sections.flatMap(section => section.fields || []).map(field => [field.id, field]));
  const errors = Object.keys(answers).filter(id => !fields.has(id)).map(id => error(`answers.${id}`, 'Unknown field', id));
  fields.forEach((field, id) => {
    const value = answers[id];
    const empty = value == null || value === '' || Array.isArray(value) && value.length === 0;
    if (field.required && empty) errors.push(error(`answers.${id}`, 'This field is required', id));
    if (empty) return;
    const rules = field.validation || {};
    if (field.type === 'short_text' || field.type === 'long_text') {
      const cap = field.type === 'short_text' ? 500 : 10000;
      const maximum = Math.min(rules.max_length ?? cap, cap);
      const minimum = rules.min_length ?? 0;
      if (typeof value !== 'string' || value.length < minimum || value.length > maximum) errors.push(error(`answers.${id}`, `Answer must contain ${minimum} to ${maximum} characters`, id));
    } else if (field.type === 'number' && (typeof value !== 'number' || !Number.isFinite(value) || value < (rules.min ?? value) || value > (rules.max ?? value))) {
      errors.push(error(`answers.${id}`, 'Number is outside the allowed range', id));
    } else if (field.type === 'radio' || field.type === 'select') {
      if (!field.options.some(option => option.id === value)) errors.push(error(`answers.${id}`, 'Select an available option', id));
    } else if (field.type === 'checkbox') {
      const allowed = new Set(field.options.map(option => option.id));
      if (!Array.isArray(value) || new Set(value).size !== value.length || value.some(item => !allowed.has(item))) errors.push(error(`answers.${id}`, 'Select only available options', id));
      else if (value.length < (rules.min_selections ?? 0) || value.length > (rules.max_selections ?? allowed.size)) errors.push(error(`answers.${id}`, 'Selection count is outside the allowed range', id));
    } else if (field.type === 'date' && (!dateValue(value) || rules.earliest && value < rules.earliest || rules.latest && value > rules.latest)) {
      errors.push(error(`answers.${id}`, 'Date is outside the allowed range', id));
    }
  });
  return errors;
}

export function formatAnswer(field, value) {
  const labels = new Map((field.options || []).map(option => [option.id, option.label]));
  if (field.type === 'checkbox') return (value || []).map(item => labels.get(item) || item).join(', ');
  if (field.type === 'radio' || field.type === 'select') return labels.get(value) || value || '';
  return value == null ? '' : String(value);
}
